import React from "react";
import { auth } from "../firebaseConfig";
import "./NotesActivityFields.css";

/**
 * Single source of truth for the "private activity" note fields shared between
 * the Notes modal and the flow "Log Notes" step. Kept framework-agnostic and
 * self-contained (own `naf-` scoped styles) so it can be dropped into any view
 * without pulling in unrelated CSS.
 */
export const ACTIVITY_TYPES = [
  { id: "call", label: "Call", icon: "fa-phone", hasOutcome: true, hasSentiment: true },
  { id: "voicemail", label: "Voicemail", icon: "fa-voicemail", hasOutcome: false, hasSentiment: false },
  { id: "meeting", label: "Meeting", icon: "fa-handshake", hasOutcome: false, hasSentiment: true },
  { id: "email", label: "Email", icon: "fa-envelope", hasOutcome: false, hasSentiment: false },
  { id: "note", label: "Note", icon: "fa-sticky-note", hasOutcome: false, hasSentiment: false },
];

export const CALL_OUTCOMES = [
  { id: "connected", label: "Connected", icon: "fa-phone-volume", tone: "neutral" },
  { id: "meeting_scheduled", label: "Meeting scheduled", icon: "fa-calendar-check", tone: "positive" },
  { id: "send_info", label: "Send info", icon: "fa-paper-plane", tone: "positive" },
  { id: "not_interested", label: "Not interested", icon: "fa-thumbs-down", tone: "negative" },
  { id: "no_answer", label: "No answer", icon: "fa-phone-slash", tone: "neutral" },
  { id: "wrong_number", label: "Wrong number", icon: "fa-ban", tone: "negative" },
];

export const SENTIMENTS = [
  { id: "negative", label: "Not happy", icon: "fa-face-frown" },
  { id: "neutral", label: "Neutral", icon: "fa-face-meh" },
  { id: "positive", label: "Positive", icon: "fa-face-smile" },
];

/** Activities a template's call context can read (functions/helpers/callContext.js). */
export const AI_CONTEXT_ACTIVITIES = ["call", "meeting", "voicemail"];

export const getActivityConfig = (id) => ACTIVITY_TYPES.find(a => a.id === id) || ACTIVITY_TYPES[4];
export const getOutcomeConfig = (id) => CALL_OUTCOMES.find(o => o.id === id) || null;
export const getSentimentConfig = (id) => SENTIMENTS.find(s => s.id === id) || null;

export const getActivityPlaceholder = (activity) => {
  switch (activity) {
    case "call": return "What was said? Pain points, objections, next steps…";
    case "voicemail": return "What did you leave on the voicemail?";
    case "meeting": return "Meeting summary, decisions, action items…";
    case "email": return "Context of the email exchange…";
    default: return "Private note…";
  }
};

/**
 * Author metadata stamped onto every note so we always know who saved it
 * (the actually signed-in user — team member or owner — not the world owner).
 */
export const getNoteAuthor = () => {
  const u = auth.currentUser;
  return {
    createdByEmail: u?.email || null,
    createdByUid: u?.uid || null,
  };
};

/**
 * Build a `lead_notes` entry (type "private") from the composer state. Mirrors
 * the shape written by the Notes modal so every surface stays consistent.
 * `meta` carries optional extras such as flow provenance
 * ({ source: 'flow', flowId, flowName }).
 */
export const createPrivateNote = ({ activity, outcome, sentiment, text, excludeFromAi }, meta = {}) => ({
  id: `note_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  type: "private",
  activity: activity || "note",
  outcome: outcome || null,
  sentiment: sentiment || null,
  text: (text || "").trim(),
  ...(excludeFromAi ? { excludeFromAi: true } : {}),
  createdAt: Date.now(),
  ...getNoteAuthor(),
  ...meta,
});

/**
 * Per-note opt-out from template call context. Only rendered for activities
 * that can feed an email at all.
 */
export const AiUseToggle = ({ activity, excludeFromAi, setExcludeFromAi, disabled }) => {
  if (!setExcludeFromAi || !AI_CONTEXT_ACTIVITIES.includes(activity)) return null;
  return (
    <label className={`naf-ai-toggle ${excludeFromAi ? "naf-ai-toggle--off" : ""}`}>
      <input
        type="checkbox"
        checked={!!excludeFromAi}
        onChange={e => setExcludeFromAi(e.target.checked)}
        disabled={disabled}
      />
      <i className={`fa ${excludeFromAi ? "fa-eye-slash" : "fa-robot"}`}></i>
      <span>Don't use in AI emails</span>
    </label>
  );
};

/**
 * `lead_notes` holds an array of note objects on every current surface, but the
 * legacy Leads view saved a single free-text string into the same field. Read
 * paths must tolerate both — an object handed to React as a child takes the
 * whole view down (error #31).
 */
export const normalizeLeadNotes = (raw) => {
  const toNote = (entry, idx) => {
    if (typeof entry === "string") {
      const text = entry.trim();
      return text ? { id: `legacy_note_${idx}`, type: "private", activity: "note", text, createdAt: null } : null;
    }
    if (entry && typeof entry === "object") return entry;
    return null;
  };
  if (Array.isArray(raw)) return raw.map(toNote).filter(Boolean);
  return [toNote(raw, 0)].filter(Boolean);
};

/** Flat text of all note bodies — for previews and summaries. */
export const leadNotesToText = (raw, separator = " · ") =>
  normalizeLeadNotes(raw)
    .map(n => (n.text || "").trim())
    .filter(Boolean)
    .join(separator);

/** True when the composer holds enough to be worth saving as a note. */
export const hasNoteContent = ({ activity, outcome, sentiment, text }) =>
  activity === "note"
    ? (text || "").trim().length > 0
    : ((text || "").trim().length > 0 || !!outcome || !!sentiment);

/**
 * Reusable field set for logging a private activity (activity type, call
 * outcome, sentiment, free text). Selecting a new activity clears the outcome.
 */
const NotesActivityFields = ({
  activity, setActivity,
  outcome, setOutcome,
  sentiment, setSentiment,
  text, setText,
  excludeFromAi, setExcludeFromAi,
  inputRef, disabled, onSubmit, rows = 3,
  compact = false,
}) => {
  const cfg = getActivityConfig(activity);
  return (
    <div className={`naf ${compact ? "naf--compact" : ""}`}>
      <div className="naf-chips">
        {ACTIVITY_TYPES.map(a => (
          <button
            key={a.id}
            type="button"
            className={`naf-chip ${activity === a.id ? "naf-chip--active" : ""}`}
            onClick={() => { setActivity(a.id); setOutcome && setOutcome(null); }}
            disabled={disabled}
          >
            <i className={`fa ${a.icon}`}></i> {a.label}
          </button>
        ))}
      </div>

      {cfg.hasOutcome && (
        <div className="naf-field">
          <label className="naf-label">Call outcome</label>
          <div className="naf-outcomes">
            {CALL_OUTCOMES.map(o => (
              <button
                key={o.id}
                type="button"
                className={`naf-outcome ${outcome === o.id ? `naf-outcome--active naf-outcome--${o.tone || "neutral"}` : ""}`}
                onClick={() => setOutcome && setOutcome(outcome === o.id ? null : o.id)}
                disabled={disabled}
              >
                <i className={`fa ${o.icon}`}></i> {o.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {cfg.hasSentiment && (
        <div className="naf-field">
          <label className="naf-label">How receptive was the contact?</label>
          <div className="naf-sentiments">
            {SENTIMENTS.map(s => (
              <button
                key={s.id}
                type="button"
                className={`naf-sentiment naf-sentiment--${s.id} ${sentiment === s.id ? "naf-sentiment--active" : ""}`}
                onClick={() => setSentiment && setSentiment(sentiment === s.id ? null : s.id)}
                disabled={disabled}
              >
                <i className={`fa ${s.icon}`}></i>
                <span>{s.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <textarea
        ref={inputRef}
        className="naf-input"
        placeholder={getActivityPlaceholder(activity)}
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onSubmit?.(); } }}
        rows={rows}
        disabled={disabled}
      />

      <AiUseToggle
        activity={activity}
        excludeFromAi={excludeFromAi}
        setExcludeFromAi={setExcludeFromAi}
        disabled={disabled}
      />
    </div>
  );
};

export default NotesActivityFields;
